import InputBase, { InputBaseProps } from "@material-ui/core/InputBase"
import { css, cx } from "@emotion/css"
import { formatFloatingPointNumber, parseNumber } from "utils/number-format"

import InputAdornment from "@material-ui/core/InputAdornment"
import React from "react"
import { Tooltip } from "@material-ui/core"
import { getCurrencySymbolFromCurrencyCode } from "utils/currencies"
import { useRifm } from "rifm"

const ghostMoneysStyles = {
  root: css`
    align-items: baseline;
  `,
  input: css`
    padding: 0.5rem 0.5rem;
    border-radius: 4px;
    border: 1px dashed transparent;
    font-size: var(--font-size, 1.125rem);
    font-weight: var(--font-weight, normal);
    :enabled:hover {
      border-color: var(--primary-main);
    }
    :focus {
      border-color: var(--primary-main);
    }
  `,
  helper: css`
    padding: var(--spacing-xxs);
  `,
  error: css`
    border-radius: 4px;
    border: 1px solid var(--red);
  `,
  currency: css`
    margin: 0;
    > p {
      font-size: 24px;
      font-weight: bold;
    }
  `,
}

type GhostMoneysProps = Omit<InputBaseProps, "onChange"> & {
  name: string
  setValue: (v: string) => void
  value?: string
  defaultValue?: string
  currency?: string
  minDigits?: number
  maxDigits?: number
  helperText?: string
  selectOnFocus?: boolean
  hasTooltip?: boolean
}

export function GhostMoneys(props: GhostMoneysProps) {
  const {
    name,
    value = "",
    setValue,
    classes,
    minDigits,
    selectOnFocus,
    error,
    helperText,
    currency,
    hasTooltip = false,
    maxDigits = 2,
    ...rest
  } = props

  const rifm = useRifm({
    value: value + "",
    accept: /[\d.]/g,
    onChange: (v) => setValue(parseNumber(v)),
    format: formatFloatingPointNumber(maxDigits, minDigits),
  })

  const onFocusSelect = (
    evt: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const inp = evt.target
    inp && inp.setSelectionRange(0, inp?.value?.length || 0)
  }

  const currencySymbol = getCurrencySymbolFromCurrencyCode(currency)

  return (
    <div>
      <Tooltip
        classes={{ tooltip: "BodyBody1Regular" }}
        title={hasTooltip ? rifm.value : ""}
      >
        <InputBase
          data-testid="money-input"
          autoComplete="off"
          classes={{
            root: cx(ghostMoneysStyles.root, classes?.root),
            input: cx(ghostMoneysStyles.input, classes?.input),
            error: cx(ghostMoneysStyles.error, classes?.error),
          }}
          {...rest}
          error={!!error}
          type="tel"
          name={name}
          value={rifm.value}
          onChange={rifm.onChange}
          onFocus={selectOnFocus ? onFocusSelect : undefined}
          startAdornment={
            <InputAdornment
              position="start"
              className={ghostMoneysStyles.currency}
            >
              {currencySymbol}
            </InputAdornment>
          }
        />
      </Tooltip>

      {!!error ? (
        <p className={cx("BodyBody-xsRedRegular", ghostMoneysStyles.helper)}>
          {helperText}
        </p>
      ) : null}
    </div>
  )
}
